import React, { useState } from "react";
import { Dialog } from "../../../../../../ui/components/Dialog";
import { Button } from "../../../../../../ui/components/Button";
import { TextField } from "../../../../../../ui/components/TextField";
import { FeatherX } from "@subframe/core";

interface SavePivotViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (name: string) => void;
  suggestedName?: string;
  isSaving?: boolean;
}

export const SavePivotViewModal: React.FC<SavePivotViewModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  suggestedName = "My Pivot View",
  isSaving = false,
}) => {
  const [viewName, setViewName] = useState(suggestedName);
  const [error, setError] = useState("");

  React.useEffect(() => {
    if (isOpen) {
      setViewName(suggestedName);
      setError("");
    }
  }, [isOpen, suggestedName]);

  const handleConfirm = () => {
    if (!viewName.trim()) {
      setError("Please enter a name for the pivot view");
      return;
    }
    onConfirm(viewName.trim());
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <Dialog.Content className="min-w-[500px] max-w-[600px]">
        <div className="flex flex-col gap-6 p-6">
          {/* Header */}
          <div className="flex items-center justify-between pb-4 border-b border-neutral-border">
            <div>
              <h3 className="text-heading-3 font-heading-3 text-default-font mb-1">
                Save Pivot View
              </h3>
              <p className="text-body font-body text-subtext-color">
                Give your pivot view configuration a name so you can reuse it later
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-subtext-color hover:text-default-font transition-colors"
            >
              <FeatherX className="w-5 h-5" />
            </button>
          </div>

          {/* View Name Input */}
          <div>
            <TextField label="Pivot View Name" error={!!error} helpText={error}>
              <TextField.Input
                value={viewName}
                onChange={(e) => {
                  setViewName(e.target.value);
                  setError("");
                }}
                placeholder="Enter a name for this pivot view"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleConfirm();
                  }
                }}
              />
            </TextField>
          </div>

          {/* Action Buttons */}
              <div className="flex justify-end gap-2 pt-4 border-t border-neutral-border">
                <Button variant="neutral-tertiary" onClick={onClose} disabled={isSaving}>
                  Cancel
                </Button>
                <Button variant="brand-primary" onClick={handleConfirm} disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save & View Results"}
                </Button>
              </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};

